/*
  TYOHAAR Shop By mega menu.

  Two jobs:

  1. <tyoh-mega> — opens the desktop panel on hover, and on click or keyboard
     for anyone without a mouse. Hover has a short delay both ways, so the panel
     neither flickers open as the pointer crosses the bar nor snaps shut on the
     way down into it.

  2. Filling the lists. Nothing in the menu is typed twice:
       filter lists  read the collection's storefront filters — the product
                     metafields the collection page filters on — through
                     templates/collection.mega-menu-data.liquid.
       budget list   reads the homepage's Price bands section as Shopify renders
                     it, so the wording, links and festival match the homepage.
     Each result is kept in localStorage and drawn from there straight away on
     the next page, then refreshed quietly once the page is idle.
*/

(() => {
  if (customElements.get('tyoh-mega')) return;

  /* ------------------------------------------------------------ helpers */

  const store = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      /* private mode: fetched every time */
    }
  };

  const recall = (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  };

  const whenIdle = (fn) => {
    if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: 2500 });
    else setTimeout(fn, 1200);
  };

  // "House warming", "housewarming" and "Housewarming" are one occasion.
  const norm = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  const SMALL = new Set(['and', 'of', 'the', 'for', 'a', 'an', 'in', 'on', 'to', '&']);
  const titleCase = (text) =>
    String(text)
      .toLowerCase()
      .split(' ')
      .map((word, i) => (i > 0 && SMALL.has(word) ? word : word.charAt(0).toUpperCase() + word.slice(1)))
      .join(' ');

  // Prefer a spelling someone wrote with care ("Raksha Bandhan") over
  // "raksha bandhan" or "RAKSHA BANDHAN"; failing that, tidy the commonest.
  const niceLabel = (variants) => {
    const ranked = [...variants].sort((a, b) => b.count - a.count);
    const cared = ranked.find((v) => v.label !== v.label.toLowerCase() && v.label !== v.label.toUpperCase());
    return cared ? cared.label : titleCase(ranked[0].label);
  };

  const valueFrom = (url, param) => {
    try {
      return new URL(url, window.location.origin).searchParams.getAll(param).pop() || null;
    } catch (e) {
      return null;
    }
  };

  /*
    The editor's list, one per line:
      Diwali
      Christmas = christmas, chirstmas
    The left side is what shows; the right side, if any, is every spelling on
    the products that should count as it.
  */
  const parsePicks = (text) =>
    String(text || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [label, aliases] = line.split('=');
        const keys = (aliases ? aliases.split(',') : [label]).map(norm).filter(Boolean);
        return { label: label.trim(), keys };
      });

  /* -------------------------------------------------------- list data */

  const filterItems = (list, data) => {
    const param = list.dataset.param;
    const filter = data && Array.isArray(data.filters) && data.filters.find((f) => f.param === param);
    if (!filter) return [];

    const groups = new Map();
    filter.values.forEach((v) => {
      const value = valueFrom(v.add, param);
      if (!value) return;
      const key = norm(v.label);
      const group = groups.get(key) || { variants: [], count: 0 };
      group.variants.push({ label: v.label, value, count: v.count });
      group.count += v.count;
      groups.set(key, group);
    });

    let items;
    const picks = parsePicks(list.dataset.pick);
    if (picks.length) {
      items = picks
        .map((pick) => {
          const found = pick.keys.map((key) => groups.get(key)).filter(Boolean);
          if (!found.length) return null;
          return {
            label: pick.label,
            values: found.flatMap((g) => g.variants.map((v) => v.value)),
          };
        })
        .filter(Boolean);
    } else {
      const all = [...groups.values()].map((g) => ({
        label: niceLabel(g.variants),
        values: g.variants.map((v) => v.value),
        count: g.count,
      }));
      // 'filter' keeps the order Search & Discovery gives the values.
      if (list.dataset.sort === 'alpha') all.sort((a, b) => a.label.localeCompare(b.label));
      else if (list.dataset.sort === 'count') all.sort((a, b) => b.count - a.count);
      items = all;
    }

    const max = parseInt(list.dataset.max, 10);
    if (max > 0) items = items.slice(0, max);

    const base = list.dataset.collection || data.url || '/collections/all';
    return items.map((item) => ({
      label: item.label,
      href: `${base}?${item.values.map((v) => `${encodeURIComponent(param)}=${encodeURIComponent(v)}`).join('&')}`,
    }));
  };

  const budgetItems = (root, strip) => {
    const cut = String(strip || '').trim().toLowerCase();
    return Array.from(root.querySelectorAll('.pb__card'))
      .map((card) => {
        let label = (card.querySelector('.pb__name')?.textContent || '').replace(/\s+/g, ' ').trim();
        if (cut && label.toLowerCase().startsWith(`${cut} `)) {
          label = label.slice(cut.length).trim();
          label = label.charAt(0).toUpperCase() + label.slice(1);
        }
        return { label, href: card.getAttribute('href') || '#' };
      })
      .filter((item) => item.label && item.href !== '#');
  };

  /* ----------------------------------------------------- fetching */

  const fetchText = async (url) => {
    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      return response.ok ? await response.text() : null;
    } catch (e) {
      return null;
    }
  };

  const withParam = (url, query) => `${url}${url.includes('?') ? '&' : '?'}${query}`;

  const sectionSelector = (source) => `[id^="shopify-section-"][id$="__${CSS.escape(source)}"]`;

  // The homepage section, as Shopify renders it now. Its id carries the
  // template's own number, so it is learnt once from the homepage itself.
  const homepageSection = async (source) => {
    const onPage = document.querySelector(sectionSelector(source));
    if (onPage) return onPage;

    const root = window.Shopify?.routes?.root || '/';
    const idKey = `tyoh-section-id:${source}`;
    const known = recall(idKey);
    let html = known ? await fetchText(withParam(root, `section_id=${encodeURIComponent(known)}`)) : null;

    if (!html) {
      const page = await fetchText(root);
      if (!page) return null;
      const found = new DOMParser().parseFromString(page, 'text/html').querySelector(sectionSelector(source));
      if (!found) return null;
      store(idKey, found.id.replace(/^shopify-section-/, ''));
      return found;
    }
    return new DOMParser().parseFromString(html, 'text/html').body;
  };

  /* ------------------------------------------------------ drawing */

  const draw = (list, items) => {
    const chips = list.classList.contains('tymm__list--chips');
    list.replaceChildren(
      ...items.map((item) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = chips ? 'tymm__chip' : 'tymm__link';
        a.href = item.href;
        a.textContent = item.label;
        li.append(a);
        return li;
      })
    );
    const column = list.closest('[data-mega-col]');
    if (column && !window.Shopify?.designMode) column.hidden = items.length === 0;
  };

  const lists = () => Array.from(document.querySelectorAll('[data-mega-list]'));

  // Fetched once per page: a first visit with nothing saved fetches straight
  // away, and the idle refresh must not fetch the same thing again.
  const fetched = new Set();

  const loadFilters = async (collectionUrl, fresh) => {
    const key = `tyoh-mega:filters:${collectionUrl}`;
    const paint = (json) => {
      let data;
      try {
        data = JSON.parse(json);
      } catch (e) {
        return;
      }
      lists()
        .filter((l) => l.dataset.megaList === 'filter' && l.dataset.collection === collectionUrl)
        .forEach((l) => draw(l, filterItems(l, data)));
    };

    const cached = recall(key);
    if (cached && !fresh) {
      paint(cached);
      return;
    }
    if (fetched.has(key)) return;
    fetched.add(key);
    const json = await fetchText(withParam(collectionUrl, 'view=mega-menu-data'));
    if (!json) return;
    try {
      JSON.parse(json);
    } catch (e) {
      return;
    }
    store(key, json);
    if (json !== cached) paint(json);
  };

  const loadBudget = async (source, fresh) => {
    const key = `tyoh-mega:budget:${source}`;
    const targets = () => lists().filter((l) => l.dataset.megaList === 'budget' && l.dataset.source === source);
    // Kept raw (label + link from the homepage); each list applies its own trim.
    const paint = (json) => {
      let raw;
      try {
        raw = JSON.parse(json);
      } catch (e) {
        return;
      }
      targets().forEach((l) => {
        const holder = document.createElement('div');
        raw.forEach((item) => {
          const card = document.createElement('a');
          card.className = 'pb__card';
          card.setAttribute('href', item.href);
          const name = document.createElement('span');
          name.className = 'pb__name';
          name.textContent = item.label;
          card.append(name);
          holder.append(card);
        });
        draw(l, budgetItems(holder, l.dataset.strip));
      });
    };

    const cached = recall(key);
    if (cached && !fresh) {
      paint(cached);
      return;
    }
    if (fetched.has(key)) return;
    fetched.add(key);
    const section = await homepageSection(source);
    if (!section) return;
    const json = JSON.stringify(budgetItems(section, ''));
    store(key, json);
    if (json !== cached) paint(json);
  };

  const fill = (fresh) => {
    const all = lists();
    const collections = new Set(all.filter((l) => l.dataset.megaList === 'filter').map((l) => l.dataset.collection));
    const sources = new Set(all.filter((l) => l.dataset.megaList === 'budget').map((l) => l.dataset.source));
    collections.forEach((url) => loadFilters(url, fresh));
    sources.forEach((source) => loadBudget(source, fresh));
  };

  let filled = false;
  const start = () => {
    if (filled) return;
    filled = true;
    fill(false); // from the saved copy, at once
    whenIdle(() => fill(true)); // then the live data
  };

  /* ------------------------------------------------- the hover panel */

  class TyohMega extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-mega-trigger]');
      this.panel = this.querySelector('[data-mega-panel]');
      if (!this.trigger || !this.panel) return;

      this.onEnter = (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.open(), 90);
      };
      this.onLeave = (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.close(), 180);
      };
      this.onClick = (event) => {
        event.preventDefault();
        this.isOpen ? this.close() : this.open();
      };
      this.onKey = (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          this.close();
          this.trigger.focus();
        }
      };
      this.onFocusOut = (event) => {
        if (!this.contains(event.relatedTarget)) this.close();
      };
      this.onOutside = (event) => {
        if (this.isOpen && !this.contains(event.target)) this.close();
      };
      this.onSelect = (event) => {
        if (this.panel.contains(event.target)) this.open();
      };
      this.onDeselect = (event) => {
        if (this.panel.contains(event.target)) this.close();
      };

      this.addEventListener('pointerenter', this.onEnter);
      this.addEventListener('pointerleave', this.onLeave);
      this.trigger.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('click', this.onOutside);
      document.addEventListener('shopify:block:select', this.onSelect);
      document.addEventListener('shopify:block:deselect', this.onDeselect);

      start();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.unlockPage();
      document.removeEventListener('click', this.onOutside);
      document.removeEventListener('shopify:block:select', this.onSelect);
      document.removeEventListener('shopify:block:deselect', this.onDeselect);
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    open() {
      clearTimeout(this.timer);
      if (this.isOpen) return;
      document.querySelectorAll('tyoh-mega[open]').forEach((other) => other !== this && other.close());

      // The strip from the word down to the panel (see .tyoh__mega::before).
      const word = this.trigger.getBoundingClientRect();
      const panel = this.panel.getBoundingClientRect();
      this.panel.style.setProperty('--tymm-bridge-x', `${word.left - panel.left}px`);
      this.panel.style.setProperty('--tymm-bridge-w', `${word.width}px`);
      this.panel.style.setProperty('--tymm-bridge-h', `${Math.max(0, panel.top - word.bottom + 8)}px`);

      // A tall panel scrolls inside itself, never past the bottom of the screen.
      this.panel.style.setProperty('--tymm-top', `${Math.max(0, word.bottom)}px`);

      this.setAttribute('open', '');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.lockPage();
    }

    close() {
      clearTimeout(this.timer);
      if (!this.isOpen) return;
      this.removeAttribute('open');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.unlockPage();
    }

    /*
      The page behind stays still while the panel is open — the same lock the
      header's drawer uses (body.tyoh-locked), with the scrollbar's width held
      as padding so nothing shifts sideways when it goes.
    */
    /*
      Through the theme's counted lock (assets/scroll-lock-tyohaar.js), which
      also stops Lenis. Lenis drives the page from the wheel itself, so
      overflow: hidden alone left the page scrolling behind the panel.
    */
    lockPage() {
      if (this.locked) return;
      this.locked = true;
      window.tyohScrollLock?.lock(this);
    }

    unlockPage() {
      if (!this.locked) return;
      this.locked = false;
      window.tyohScrollLock?.release(this);
    }
  }

  customElements.define('tyoh-mega', TyohMega);

  // The drawer copy has no <tyoh-mega>; make sure its lists fill too.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
